import { useTranslation } from 'react-i18next';

import { Button, Icon, Input, Modal, Text } from '@pukka/ui-components';
import debounce from 'lodash/debounce';
import { useSnapshot } from 'valtio';

import { BrandsAssignState, isDirty, isEmpty } from './BrandsAssign.state';
import { BrandSelector } from './BrandSelector';
import { ParentBrandSelector } from './ParentBrandSelector';

interface BrandsAssignModalProps {
  readonly heading: string;
  readonly description: string;
  readonly searchPlaceholder: string;
  readonly open?: boolean;
  readonly variant: 'brand' | 'parent';
  readonly isButtonLoading?: boolean;
  readonly onToggleModal?: (open: boolean) => void;
  readonly onSubmit?: () => void;
  readonly state?: typeof BrandsAssignState;
}

function BrandsAssignModal({
  open,
  heading,
  description,
  searchPlaceholder,
  variant,
  isButtonLoading,
  onToggleModal,
  onSubmit,
  state = BrandsAssignState,
}: BrandsAssignModalProps) {
  const { t } = useTranslation('library');

  const { selectedBrandList, selectedParent } = useSnapshot(state);

  const handleChangeSearch = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    state.handleChangeSearchFilter(e.target.value);
  }, 500);

  const handleClickClose = () => {
    state.handleChangeSearchFilter('');
  };

  const emptySelectedParent = variant === 'parent' && selectedParent?.id === -2;
  const isDisabled = isEmpty(variant, state) || emptySelectedParent || !isDirty(variant, state);

  const getSelectedParentName = () => {
    if (selectedParent?.id === -1 || selectedParent?.id === null) {
      return `${t('brands.assignModal.noPrincipal')} ${t('brands.assignModal.selected')}`;
    }

    if (selectedParent?.name) {
      return `${selectedParent?.name} ${t('brands.assignModal.selected')}`;
    }

    return '';
  };

  const selectedParentName = getSelectedParentName();

  return (
    <Modal unwrap open={open} onOpenChange={onToggleModal}>
      <Modal.Content className="!w-[750px]">
        <div className="px-8 py-5 ">
          <div className="flex h-16 items-center justify-between">
            <Modal.Trigger asChild>
              <Button size="s" variant="nude" className="p-0" data-testid="close-btn" disabled={isButtonLoading} onClick={handleClickClose}>
                <Icon icon="close" color="blue" />
              </Button>
            </Modal.Trigger>
            <div className="flex items-center gap-3">
              {variant === 'brand' && (
                <Text>
                  {selectedBrandList.length} {t('brands.assignModal.selected')}
                </Text>
              )}
              {variant === 'parent' && selectedParentName && <Text>{selectedParentName}</Text>}
              <Button
                size="l"
                disabled={isDisabled}
                loading={isButtonLoading}
                onClick={onSubmit}
                className="rounded-xl"
                data-testid="submit-assign"
              >
                {t('brands.assignButton')}
              </Button>
            </div>
          </div>
          <div>
            <Text variant="heading-1" weight="semibold">
              {heading}
            </Text>
            <Text>{description}</Text>
          </div>
        </div>
        <div className="my-4 px-8">
          <Input placeholder={searchPlaceholder} size="l" icon="search" reverse onChange={handleChangeSearch} />
          <div className="mt-4 h-[calc(100vh-400px)] overflow-y-auto px-4">
            {variant === 'brand' && <BrandSelector />}
            {variant === 'parent' && <ParentBrandSelector />}
          </div>
        </div>
      </Modal.Content>
    </Modal>
  );
}

export { BrandsAssignModal };
